import React from "react";
import { Helmet } from "react-helmet-async";
import { trackEvent, EventNames } from "../analytics";
import { canonicalUrl } from "../siteConfig";

export default function HomePage() {
  const trackScanCta = (ctaId) => trackEvent(EventNames.CtaClicked, { cta_id: ctaId });
  const trackAssessmentCta = (source) => trackEvent(EventNames.AssessmentCtaClicked, { source });

  return (
    <div className="app">
      <Helmet>
        <title>Find Technical SEO Issues | OptiSiteSEO</title>
        <meta
          name="description"
          content="Run a Free SEO Scan to identify indexing, crawlability, performance, and discoverability issues. Request a Technical SEO & Visibility Assessment for a prioritized implementation roadmap."
        />
        <link rel="canonical" href={canonicalUrl("/")} />
        <meta property="og:title" content="Find Technical SEO Issues | OptiSiteSEO" />
        <meta
          property="og:description"
          content="OptiSiteSEO helps businesses find technical SEO issues that may be limiting indexing, crawlability, performance, and website visibility."
        />
        <meta property="og:url" content={canonicalUrl("/")} />
      </Helmet>

      <nav className="top-nav">
        <div className="brand">
          <span>OptiSiteSEO</span>
        </div>
        <div className="nav-links">
          <a className="link" href="/">
            Home
          </a>
          <a className="link" href="/demo/index.html">
            Sample report
          </a>
          <a className="link" href="/how-it-works/index.html">
            How it works
          </a>
          <a className="link" href="/technical-visibility-audit">
            Assessment Details
          </a>
          <a className="secondary" href="/app/index.html?signin=1">
            Sign in
          </a>
          <a className="primary" href="/app/index.html" onClick={() => trackScanCta("home-nav-scan")}>
            Run Free SEO Scan
          </a>
        </div>
      </nav>

      <header className="service-hero" id="hero">
        <div className="service-hero-copy">
          <p className="eyebrow">Technical SEO + visibility infrastructure</p>
          <h1>Find the technical SEO issues limiting your website&apos;s visibility.</h1>
          <p className="subtext">
            Run a free scan to identify indexing, crawlability, performance, and discoverability issues. Get a clear view of what may be holding
            your site back and what to fix first.
          </p>
          <div className="hero-actions">
            <a className="primary" href="/app/index.html" onClick={() => trackScanCta("home-hero-scan")}>
              Run Free SEO Scan
            </a>
            <a className="secondary" href="/request-assessment" onClick={() => trackAssessmentCta("home_hero")}>
              Request Assessment
            </a>
          </div>
          <div className="badge-row">
            <span className="badge">Free SEO Scan</span>
            <span className="badge">Technical SEO signals</span>
            <span className="badge">No outcome guarantees</span>
          </div>
        </div>
        <aside className="offer-summary" aria-label="Technical SEO & Visibility Assessment summary">
          <p className="eyebrow">Paid diagnostic</p>
          <h2>Technical SEO & Visibility Assessment</h2>
          <p>
            Manual review of scan results, priority pages, structured data, performance, analytics, and the fixes most likely to matter first.
          </p>
          <div className="offer-facts">
            <div>
              <span className="label">Assessment</span>
              <strong>$297-$1,500</strong>
            </div>
            <div>
              <span className="label">Output</span>
              <strong>Implementation roadmap</strong>
            </div>
          </div>
          <a className="secondary" href="/request-assessment" onClick={() => trackAssessmentCta("home_offer_summary")}>
            Request Assessment
          </a>
        </aside>
      </header>

      <section className="card section compact-section">
        <div className="section-header">
          <div>
            <p className="eyebrow">What you get</p>
            <h2>A quick read on what is limiting growth</h2>
          </div>
          <a className="primary" href="/app/index.html" onClick={() => trackScanCta("home-what-you-get-scan")}>
            Run Free SEO Scan
          </a>
        </div>
        <ul className="bullets">
          <li>Technical SEO signals across indexing, crawlability, metadata, structured data, performance, and page structure</li>
          <li>Issues such as unclear priority pages, weak internal paths, and missing measurement context</li>
          <li>Plain-language notes on what each issue means for visibility and lead flow</li>
          <li>A sample report path before you request a paid technical SEO assessment</li>
        </ul>
      </section>

      <section className="info-columns section">
        <div className="plain-panel">
          <p className="eyebrow">Visibility problems</p>
          <h2>Can the right people find the site?</h2>
          <ul className="bullets">
            <li>Indexing signals like robots, canonicals, redirects, sitemap, and reachability</li>
            <li>Metadata, headings, page structure, mobile basics, and Core Web Vitals risk hints</li>
            <li>Issues that can keep service pages, location pages, or key offers from being understood</li>
          </ul>
        </div>
        <div className="plain-panel">
          <p className="eyebrow">Growth bottlenecks</p>
          <h2>Can visitors tell what to do next?</h2>
          <ul className="bullets">
            <li>Weak CTAs, unclear conversion paths, and missing next-step signals</li>
            <li>Thin or missing service pages, local presence gaps, and confusing page hierarchy</li>
            <li>Problems that may turn visits into missed inquiries instead of useful next steps</li>
          </ul>
        </div>
        <div className="plain-panel">
          <p className="eyebrow">Prioritized action</p>
          <h2>What matters first?</h2>
          <ul className="bullets">
            <li>Manual review of the scan findings, priority pages, and tracking setup</li>
            <li>Technical SEO fixes prioritized by likely impact, effort, and dependency order</li>
            <li>A prioritized implementation roadmap for what to do first, next, and later</li>
          </ul>
        </div>
      </section>

      <section className="card section compact-section" id="how-it-works">
        <div className="section-header">
          <div>
            <p className="eyebrow">Next steps</p>
            <h2>How It Works</h2>
          </div>
        </div>
        <div className="card-grid" aria-label="How OptiSiteSEO works">
          <div className="plain-panel">
            <p className="eyebrow">Step 1</p>
            <h3>Run a Free SEO Scan</h3>
            <p>Identify technical SEO, indexing, performance, and visibility issues.</p>
          </div>
          <div className="plain-panel">
            <p className="eyebrow">Step 2</p>
            <h3>Review the Findings</h3>
            <p>See what may be affecting discoverability, user experience, and lead generation.</p>
          </div>
          <div className="plain-panel">
            <p className="eyebrow">Step 3</p>
            <h3>Request a Technical SEO & Visibility Assessment</h3>
            <p>Get a manual review and prioritized implementation roadmap.</p>
          </div>
        </div>
        <div className="cta-row">
          <a className="primary" href="/app/index.html" onClick={() => trackScanCta("home-offer-ladder-scan")}>
            Run Free SEO Scan
          </a>
          <a className="secondary" href="/request-assessment" onClick={() => trackAssessmentCta("home_offer_ladder")}>
            Request Assessment
          </a>
          <a className="secondary" href="/technical-visibility-audit">
            View Assessment Details
          </a>
        </div>
      </section>

      <footer>
        <p>OptiSiteSEO helps businesses find technical SEO issues, improve visibility, and decide what to fix first. First scan is free.</p>
        <p style={{ color: "#7380a8", marginTop: 8 }}>(c) {new Date().getFullYear()} OptiSiteSEO. All rights reserved.</p>
      </footer>
    </div>
  );
}
